import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { loadSoftwaresAndServices } from 'services/software';
import { IService } from 'shared/models/service';
import { contractFilter } from './ContractsListFilters';

type PeriodPreset = 'all' | 'today' | '7days' | '15days' | '30days' | 'custom';

type StatusOption = 'all' | 'active' | 'deactivated' | 'canceled';

interface ContractsFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filter: contractFilter) => void;
  currentFilter: contractFilter;
}

const getDateDaysAgo = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().split('T')[0];
};

const getToday = (): string => new Date().toISOString().split('T')[0];

const mapStatusToFilter = (
  status: StatusOption,
): { status: 0 | 1 | null; includeDeleted: boolean } => {
  switch (status) {
    case 'active':
      return { status: 1, includeDeleted: false };
    case 'deactivated':
      return { status: 0, includeDeleted: false };
    case 'canceled':
      return { status: null, includeDeleted: true };
    default:
      return { status: null, includeDeleted: true };
  }
};

const deriveStatusOption = (filter: contractFilter): StatusOption => {
  if (filter.status === 1) return 'active';
  if (filter.status === 0) return 'deactivated';
  return 'all';
};

const derivePeriodPreset = (filter: contractFilter): PeriodPreset => {
  if (!filter.startDate && !filter.endDate) return 'all';
  const today = getToday();
  if (filter.startDate === today && filter.endDate === today) return 'today';
  if (filter.startDate === getDateDaysAgo(7) && filter.endDate === today)
    return '7days';
  if (filter.startDate === getDateDaysAgo(15) && filter.endDate === today)
    return '15days';
  if (filter.startDate === getDateDaysAgo(30) && filter.endDate === today)
    return '30days';
  return 'custom';
};

const ContractsFilterModal = ({
  isOpen,
  onClose,
  onApply,
  currentFilter,
}: ContractsFilterModalProps) => {
  const [periodicity, setPeriodicity] = useState(currentFilter.periodicity);
  const [statusOption, setStatusOption] = useState<StatusOption>(
    deriveStatusOption(currentFilter),
  );
  const [serviceId, setServiceId] = useState<number | undefined>(
    currentFilter.service,
  );
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>(
    derivePeriodPreset(currentFilter),
  );
  const [startDate, setStartDate] = useState(currentFilter.startDate ?? '');
  const [endDate, setEndDate] = useState(currentFilter.endDate ?? '');

  const [services, setServices] = useState<IService[]>([]);

  useEffect(() => {
    if (isOpen) {
      setPeriodicity(currentFilter.periodicity);
      setStatusOption(deriveStatusOption(currentFilter));
      setServiceId(currentFilter.service);
      setPeriodPreset(derivePeriodPreset(currentFilter));
      setStartDate(currentFilter.startDate ?? '');
      setEndDate(currentFilter.endDate ?? '');
    }
  }, [isOpen, currentFilter]);

  useEffect(() => {
    loadSoftwaresAndServices()
      .then((response) => {
        const allServices = response.data.results.flatMap(
          (software) => software.services ?? [],
        );
        const licenseServices = allServices.filter((s) => s.is_license === 1);
        setServices(licenseServices);
      })
      .catch((e: AxiosError) => {
        console.error(
          'Erro ao carregar serviços para filtro',
          e?.response?.data
            ? JSON.stringify(e?.response?.data, null, 2)
            : JSON.stringify(e?.response, null, 2),
        );
      });
  }, []);

  const handlePeriodPresetChange = useCallback((preset: PeriodPreset) => {
    setPeriodPreset(preset);
    const today = getToday();
    switch (preset) {
      case 'all':
        setStartDate('');
        setEndDate('');
        break;
      case 'today':
        setStartDate(today);
        setEndDate(today);
        break;
      case '7days':
        setStartDate(getDateDaysAgo(7));
        setEndDate(today);
        break;
      case '15days':
        setStartDate(getDateDaysAgo(15));
        setEndDate(today);
        break;
      case '30days':
        setStartDate(getDateDaysAgo(30));
        setEndDate(today);
        break;
      case 'custom':
        break;
    }
  }, []);

  const handleApply = () => {
    const { status, includeDeleted } = mapStatusToFilter(statusOption);
    onApply({
      periodicity,
      status,
      includeDeleted,
      service: serviceId,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    });
    onClose();
  };

  const handleClear = () => {
    const cleared: contractFilter = {
      periodicity: 'all',
      status: null,
      includeDeleted: true,
      service: undefined,
      startDate: undefined,
      endDate: undefined,
    };
    onApply(cleared);
    onClose();
  };

  const periodPresetButton = (
    preset: PeriodPreset,
    label: string,
    iconLeft?: string,
  ) => (
    <Button
      key={preset}
      size="sm"
      variant="stroke"
      colorScheme={periodPreset === preset ? 'selected' : ''}
      onClick={() => handlePeriodPresetChange(preset)}
      leftIcon={
        iconLeft ? (
          <span style={{ fontSize: '14px' }}>{iconLeft}</span>
        ) : undefined
      }
    >
      {label}
    </Button>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text fontSize="lg" fontWeight="bold">
            Filtros
          </Text>
          <Text fontSize="sm" fontWeight="normal" color="gray.500" mt={1}>
            Faça as configurações necessárias antes de aplicar o filtro.
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6} align="stretch">
            <Box>
              <FormLabel fontWeight="bold" mb={2}>
                Contrato
              </FormLabel>
              <HStack spacing={2}>
                {(['all', 'yearly', 'monthly'] as const).map((p) => (
                  <Button
                    key={p}
                    size="sm"
                    variant="stroke"
                    colorScheme={periodicity === p ? 'selected' : ''}
                    onClick={() => setPeriodicity(p)}
                  >
                    {p === 'all'
                      ? 'Todos'
                      : p === 'yearly'
                        ? 'Anual'
                        : 'Mensal'}
                  </Button>
                ))}
              </HStack>
            </Box>

            <HStack spacing={4} align="flex-start">
              <FormControl>
                <FormLabel fontWeight="bold">Status</FormLabel>
                <Select
                  value={statusOption}
                  onChange={(e) =>
                    setStatusOption(e.target.value as StatusOption)
                  }
                >
                  <option value="all">Todos</option>
                  <option value="active">Ativo</option>
                  <option value="deactivated">Desativado</option>
                  <option value="canceled">Cancelado</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontWeight="bold">Plano</FormLabel>
                <Select
                  value={serviceId ?? ''}
                  onChange={(e) =>
                    setServiceId(
                      e.target.value ? Number(e.target.value) : undefined,
                    )
                  }
                >
                  <option value="">Selecione uma opção</option>
                  {services.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </HStack>

            <Box>
              <FormLabel fontWeight="bold" mb={2}>
                Período
              </FormLabel>
              <HStack spacing={2} flexWrap="wrap">
                {periodPresetButton('all', 'Todos')}
                {periodPresetButton('today', 'Hoje')}
                {periodPresetButton('7days', '7 dias atrás')}
                {periodPresetButton('15days', '15 dias atrás')}
                {periodPresetButton('30days', '30 dias atrás')}
                {periodPresetButton('custom', 'Personalizado', '📅')}
              </HStack>

              <HStack spacing={4} mt={4}>
                <FormControl>
                  <FormLabel>Data inicial</FormLabel>
                  <Input
                    type="date"
                    placeholder="Ex: 01/01/2023"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setPeriodPreset('custom');
                    }}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Data final</FormLabel>
                  <Input
                    type="date"
                    placeholder="Ex: 01/01/2023"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setPeriodPreset('custom');
                    }}
                  />
                </FormControl>
              </HStack>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="outline" mr={3} onClick={handleClear}>
            Limpar
          </Button>
          <Button colorScheme="yooga" onClick={handleApply}>
            Aplicar filtro
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ContractsFilterModal;
